import _ from 'lodash';
import { createClientContext, makeInvokeBackendFunction } from '@skyjs/client-context';
import { DateTime, UTCDate } from '@skyjs/date';
import { Language } from '@skyjs/lang';
import getRequestContext from './getRequestContext.js';
import setupPubNub from './setupPubNub.js';
import dependencies from './dependencies.js';
import createPubNubMessageHandler from './createPubNubMessageHandler.js';
import RequestContextService from './RequestContextService.js';
import makeUpdateAuthToken from './authToken/makeUpdateAuthToken.js';
import redirectToLogin from './authToken/redirectToLogin.js';
import refreshAuthToken from './authToken/refreshAuthToken.js';
import startAutoRefreshAuthToken from './authToken/startAutoRefreshAuthToken.js';
import upgradeAuthToken from './authToken/upgradeAuthToken.js';

export default async function createContext(onContextChange) {
  const req = await getRequestContext();

  const invokeFunction = makeInvokeBackendFunction(req);

  const updateAuthToken = makeUpdateAuthToken(invokeFunction.updateAuthToken);
  await startAutoRefreshAuthToken(
    req.authToken,
    req.authTokenExpirationDate,
    req.refreshToken,
    updateAuthToken
  );

  const wrappedInvokeFunction = await wrapWithAuthenticationHandling(
    invokeFunction,
    updateAuthToken
  );

  const { app, currentUser } = await wrappedInvokeFunction('app');
  invokeFunction.setVerbose(app.version === 'dev');

  if (currentUser.language) {
    Language.current = _.lowerCase(currentUser.language);
  }
  UTCDate.defaultLocale = Language.current;
  DateTime.defaultLocale = Language.current;

  const context = createClientContext({
    app,
    currentUser,
    invokeFunction: wrappedInvokeFunction,
    dependencies,
    log: {
      info: () => {},
      error: err => {
        console.error(err);
      }
    },
    isPendingMFA: req.isPendingMFA
  });

  if (currentUser.id !== 'anonymous') {
    const pubNubMessageHandler = createPubNubMessageHandler(onContextChange, context);
    await setupPubNub(context.getEventSubscriptionInfo, pubNubMessageHandler, () => {
      context.reset();
      onContextChange({ ...context });
    });
  }

  Object.assign(context, { requestContextService: RequestContextService });

  return context;
}

async function wrapWithAuthenticationHandling(invokeFunction, onAuthTokenUpdated) {
  let refreshingAuthToken;

  const processRequest = async (...args) => {
    if (refreshingAuthToken) {
      await refreshingAuthToken;
    }

    return invokeFunction(...args);
  };

  return async (...args) => {
    try {
      const result = await processRequest(...args);

      if (result.deprecatedAuthTokenWarning && refreshingAuthToken == null) {
        refreshingAuthToken = upgradeAuthToken()
          .then(({ authToken, authTokenExpirationDate, refreshToken }) =>
            onAuthTokenUpdated(authToken, authTokenExpirationDate, refreshToken)
          )
          .catch(() => {})
          .finally(() => {
            refreshingAuthToken = undefined;
          });
      }

      return result.data;
    } catch (e) {
      if (e.response && e.response.status === 401) {
        const requestContext = RequestContextService.get();

        if (
          requestContext?.refreshToken == null &&
          requestContext?.authTokenExpirationDate == null
        ) {
          await redirectToLogin();
        } else {
          if (refreshingAuthToken == null) {
            refreshingAuthToken = refreshAuthToken()
              .then(({ authToken, authTokenExpirationDate, refreshToken }) =>
                onAuthTokenUpdated(authToken, authTokenExpirationDate, refreshToken)
              )
              .catch(err => {
                if (err.status === 401) {
                  return redirectToLogin();
                } else {
                  throw err;
                }
              })
              .finally(() => {
                refreshingAuthToken = undefined;
              });
          }

          return refreshingAuthToken.then(async () => {
            const result = await processRequest(...args);
            return result.data;
          });
        }
      }

      throw (e.response && e.response.data) || e;
    }
  };
}
